import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  X, 
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';

export const Tickets: React.FC = () => {
  const { user, profile } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!profile) return;

    const q = query(
      collection(db, 'tickets'),
      where('clientId', '==', profile.uid),
      where('tenantId', '==', profile.tenantId || ''),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tickets');
    });

    return () => unsubscribe();
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const timestamp = new Date().toISOString();
      const initialHistory = [
        {
          previous_status: '',
          new_status: 'new',
          changed_by: user.uid,
          changed_at: timestamp,
          note: 'Request initiated by client.',
          status: 'new',
          updatedAt: timestamp,
          changedByName: profile?.displayName || 'Client'
        }
      ];

      const ticketData = {
        clientId: user.uid,
        contact_id: user.uid,
        subject,
        category,
        priority,
        status: 'new',
        created_at: timestamp,
        updated_at: timestamp,
        createdAt: timestamp,
        updatedAt: timestamp,
        converted_to_client_at: null,
        status_history: initialHistory,
        statusHistory: initialHistory,
        assigned_staff_id: null,
        assignedTo: null,
        tenantId: profile?.tenantId || '',
      };

      const docRef = await addDoc(collection(db, 'tickets'), ticketData);

      // Create initial conversation for the ticket
      await addDoc(collection(db, 'conversations'), {
        participants: [user.uid], // Staff will join later
        ticketId: docRef.id,
        type: 'ticket',
        updatedAt: new Date().toISOString(),
        lastMessage: { text: description, senderId: user.uid, createdAt: new Date().toISOString() },
        tenantId: profile?.tenantId || '',
      });

      // Add the first message to the conversation
      // In a real app, we'd get the conversation ID first. 
      // For simplicity, we'll just close the modal.

      setIsModalOpen(false);
      setSubject('');
      setDescription('');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'tickets');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || t.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support Requests</h1>
          <p className="text-gray-400">Manage and track your technical and business inquiries.</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-2xl font-bold transition-all active:scale-95"
        >
          <Plus size={20} />
          New Request
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Search by subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-white/5 rounded-2xl pl-12 pr-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-all"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0A0A0A] border border-white/5 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-all appearance-none min-w-[140px]"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="in_review">In Review</option>
            <option value="qualified">Qualified</option>
            <option value="proposal_sent">Proposal Sent</option>
            <option value="won">Won</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5 text-xs font-mono text-gray-500 uppercase tracking-widest">
                <th className="px-6 py-4 font-medium">Ticket ID</th>
                <th className="px-6 py-4 font-medium">Subject</th>
                <th className="px-6 py-4 font-medium">Category</th>
                <th className="px-6 py-4 font-medium">Priority</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Created</th>
                <th className="px-6 py-4 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4 text-xs font-mono text-gray-500">#{ticket.id.slice(0, 8)}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium">{ticket.subject}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-gray-400 capitalize">{ticket.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                        ticket.priority === 'urgent' ? 'text-red-500 bg-red-500/10' :
                        ticket.priority === 'high' ? 'text-orange-500 bg-orange-500/10' :
                        ticket.priority === 'medium' ? 'text-blue-500 bg-blue-500/10' :
                        'text-gray-500 bg-gray-500/10'
                      }`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {ticket.status === 'new' && <Clock size={14} className="text-cyan-500" />}
                        {ticket.status === 'in_review' && <AlertCircle size={14} className="text-yellow-500" />}
                        {ticket.status === 'qualified' && <CheckCircle2 size={14} className="text-indigo-500" />}
                        {ticket.status === 'proposal_sent' && <CheckCircle2 size={14} className="text-purple-500" />}
                        {ticket.status === 'won' && <CheckCircle2 size={14} className="text-green-500" />}
                        {ticket.status === 'active' && <Clock size={14} className="text-blue-500" />}
                        {ticket.status === 'closed' && <CheckCircle2 size={14} className="text-gray-500" />}
                        <span className="text-xs capitalize">{ticket.status.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-gray-500 hover:text-white transition-colors">
                        <ChevronRight size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-20 text-center text-gray-500 text-sm">
                    No tickets found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-white/5">
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => (
              <div key={ticket.id} className="p-6 space-y-4 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-gray-500">#{ticket.id.slice(0, 8)}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                    ticket.priority === 'urgent' ? 'text-red-500 bg-red-500/10' :
                    ticket.priority === 'high' ? 'text-orange-500 bg-orange-500/10' :
                    ticket.priority === 'medium' ? 'text-blue-500 bg-blue-500/10' :
                    'text-gray-500 bg-gray-500/10'
                  }`}>
                    {ticket.priority}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-bold mb-1">{ticket.subject}</h3>
                  <p className="text-xs text-gray-500 capitalize">{ticket.category}</p>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    {ticket.status === 'new' && <Clock size={14} className="text-cyan-500" />}
                    {ticket.status === 'in_review' && <AlertCircle size={14} className="text-yellow-500" />}
                    {ticket.status === 'qualified' && <CheckCircle2 size={14} className="text-indigo-500" />}
                    {ticket.status === 'proposal_sent' && <CheckCircle2 size={14} className="text-purple-500" />}
                    {ticket.status === 'won' && <CheckCircle2 size={14} className="text-green-500" />}
                    {ticket.status === 'active' && <Clock size={14} className="text-blue-500" />}
                    {ticket.status === 'closed' && <CheckCircle2 size={14} className="text-gray-500" />}
                    <span className="text-xs capitalize">{ticket.status.replace('_', ' ')}</span>
                  </div>
                  <span className="text-[10px] text-gray-500">
                    {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-20 text-center text-gray-500 text-sm">
              No tickets found matching your criteria.
            </div>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl rounded-3xl overflow-hidden relative z-10 shadow-2xl"
            >
              <div className="p-8 border-b border-white/5 flex items-center justify-between">
                <h2 className="text-2xl font-bold">New Support Request</h2>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Category</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                    >
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing & Payments</option>
                      <option value="account">Account Access</option>
                      <option value="event">Event Support</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Priority</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Subject</label>
                  <input
                    type="text"
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Briefly describe the issue"
                    className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Description</label>
                  <textarea
                    required
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide detailed information about your request..."
                    className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50"
                  >
                    {isSubmitting ? 'Creating Request...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
